import { useState } from 'react';
import { toast } from 'react-toastify';
import { ManagedAccountsApi } from '@/api/clients/managed-accounts/managed-accounts.api';
import useFormValidation from '@/hooks/useFormValidation';
import { SFTP_AUTHENTICATION_MODE } from '../paymentsManagerConfiguration.constants';
import { toPaymentsManagerForm } from '../paymentsManagerConfiguration.utils';
import { useReconPaths } from './useReconPaths';

export const usePaymentsManagerConfigurationForm = ({
  managedAccountId,
  configuration,
  onSaved,
}) => {
  const [form, setForm] = useState(() => toPaymentsManagerForm(configuration));
  const [passwordConfigured, setPasswordConfigured] = useState(
    configuration?.passwordConfigured ?? false,
  );
  const [privateKeyConfigured, setPrivateKeyConfigured] = useState(
    configuration?.privateKeyConfigured ?? false,
  );
  const [saving, setSaving] = useState(false);
  const [hasSavedConfiguration, setHasSavedConfiguration] = useState(
    Boolean(configuration),
  );
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation();
  const recon = useReconPaths(configuration?.reconPaths ?? [], clearFieldError);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    clearFieldError(name);
  };

  const updateAuthenticationMode = (authenticationMode) => {
    setForm((current) => {
      let password = '';
      let privateKey = '';
      if (authenticationMode === SFTP_AUTHENTICATION_MODE.PASSWORD) {
        password = current.password;
      }
      if (authenticationMode === SFTP_AUTHENTICATION_MODE.SSH_KEY) {
        privateKey = current.private_key;
      }

      return {
        ...current,
        authentication_mode: authenticationMode,
        password,
        private_key: privateKey,
      };
    });
    clearFieldError('authentication_mode');
    clearFieldError('password');
    clearFieldError('private_key');
  };

  const save = async (event) => {
    event.preventDefault();
    clearAllErrors();
    setSaving(true);

    try {
      const response = await ManagedAccountsApi.updatePaymentsManager(
        managedAccountId,
        {
          ...form,
          port: Number(form.port),
          recon_paths: recon.reconPathValues(),
        },
      );
      const saved = response.data.data.configuration;
      setForm(toPaymentsManagerForm(saved));
      recon.setSavedReconPaths(saved.reconPaths);
      setPasswordConfigured(saved.passwordConfigured);
      setPrivateKeyConfigured(saved.privateKeyConfigured);
      setHasSavedConfiguration(true);
      onSaved();
      toast.success('Payments Manager configuration saved');
    } catch (error) {
      setBackendErrors(error.response?.data);
      if (!error.response?.data?.message) {
        toast.error('Failed to save Payments Manager configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    fieldErrors,
    generalErrors,
    passwordConfigured,
    privateKeyConfigured,
    saving,
    hasSavedConfiguration,
    reconPaths: recon.reconPaths,
    updateField,
    updateAuthenticationMode,
    addReconPath: recon.addReconPath,
    updateReconPath: recon.updateReconPath,
    removeReconPath: recon.removeReconPath,
    save,
  };
};
